/**
 * RenderDisplayable — thin React wrapper that translates a
 * `Displayable` (the typed displayable model emitted by
 * `renderSegments`) to JSX. Keeps `renderSegments.ts` testable
 * from Node without React.
 *
 * Spec: chores docs/plans/elka-display-spec.md §5.2.
 */

import React from "react";
import type { Displayable } from "./renderSegments";

export interface RenderDisplayableProps {
  runs: Displayable;
  /**
   * Optional className applied to a wrapping <span>. When omitted
   * the component renders a React.Fragment so it composes inside
   * existing flow-content cells without adding box-model nesting.
   */
  className?: string;
}

/**
 * Render the displayable model. Adjacent runs with byte-equal
 * style are NOT coalesced here — renderSegments may do that as
 * an optional cleanup pass; the wrapper just emits one <span>
 * per run.
 */
export function RenderDisplayable({ runs, className }: RenderDisplayableProps): React.ReactElement {
  const elements = runs.map((run, i) => {
    if (!run.style) {
      // No style — emit plain text. Using <span> rather than
      // bare text keeps the key prop honest for React's
      // reconciler.
      return <span key={i}>{run.text}</span>;
    }
    const cssStyle: React.CSSProperties = {};
    if (run.style.color) cssStyle.color = run.style.color;
    if (run.style.weight) cssStyle.fontWeight = run.style.weight as React.CSSProperties["fontWeight"];
    if (run.style.italic === true) cssStyle.fontStyle = "italic";
    if (run.style.italic === false) cssStyle.fontStyle = "normal";
    return (
      <span key={i} style={cssStyle}>
        {run.text}
      </span>
    );
  });
  if (className !== undefined) {
    return <span className={className}>{elements}</span>;
  }
  return <>{elements}</>;
}
